import $ from 'init/jquery'
import { KEY_CODES } from 'es6/constants'

/*
 *
 * Copyright 2012 Art. Lebedev Studio. All Rights Reserved.
 * Created on 2012.03.06
 *
 *
 */

/**
 * @fileoverview Class representing popup - block which display is toggled
 * by clicks on opener element.
 * Popup can be hid:
 *  - by click on inner .popup_close element (Popup.CLASS_CLOSE_BUTTON);
 *  - by click on external clicks catcher (see constructor 3rd parameter);
 *  - by hitting Escape key on keyboard.
 *
 *  Class is looking for .popup_positioner (Popup.CLASS_POSITIONER) elements
 *  inside popup root and inside opener to determine elements that should
 *  be matched visually after popup is opened.
 *  If there is no such element inside opener, opener itself is used.
 *  If there is no such element inside popup root, no auto positioning
 *  is performed.
 *
 * @author Alexander Samilyak (aleksam241@gmail.com)
 */

/**
 * @param {!jQuery} root  Popup root element.
 * @param {!jQuery=} opt_opener  Element click on which toggles popup display.
 *    Defaults to null (there is no opener element).
 * @param {!jQuery=} opt_externalClickCatcher  Element click on which
 *    closes popup (if click was outside popup element).
 *    Defaults to document - any click outside popup root element closes it.
 * @param {(string | number)=} opt_duration  Time for toggling popup display.
 *    This parameter is passing to jQuery's animate() function.
 *    Defaults to null - there is no animation, just toggling `display:none`.
 * @param {!jQuery=} opt_excludeExternalClickCatcher  Element click on which
 *    don't closes popup (if click was outside popup element).
 *    Defaults to document - any click outside popup root element closes it.
 *  @param {!jQuery} positionerParent  Popup positioner parent element.
 * @constructor
 */
export const Popup = function (
  root,
  opt_opener,
  opt_externalClickCatcher,
  opt_duration,
  opt_excludeExternalClickCatcher,
  positionerParent) {
  /**
   * @type {!jQuery}
   * @private
   */
  this.root_ = root

  /**
   * @type {jQuery}
   * @public
   */
  this.opener_ = opt_opener || null

  /**
   * @private
   */
  this.closer_ =
    (/** @type {!jQuery} */ this.root_.find(
      '.' + Popup.CLASS_CLOSE_BUTTON))

  /**
   * @type {jQuery}
   * @private
   */
  this.positionerInsidePopup_ = null

  /**
   * @type {jQuery}
   * @private
   */
  this.positionerOutsidePopup_ = null

  /**
   * @private
   */
  this.externalClickCatcher_ = $(opt_externalClickCatcher || document)

  /**
   * @type {!jQuery}
   * @private
   */
  this.exclideExternalClickCatcher_ = opt_excludeExternalClickCatcher

  /**
   * @type {?(string|number)}
   * @private
   */
  this.duration_ = opt_duration || null

  /**
   * @private
   */
  this.eventsDispatcher_ = /** @type {!jQuery} */ ($({}))

  /**
   * @type {boolean}
   * @private
   */
  this.closed_ = (
    this.root_.hasClass(Popup.CLASS_NOT_DISPLAY) || !this.root_.is(':visible'))

  /**
   * @type {boolean}
   * @private
   */
  this.aniInProgress_ = false

  this.positionerParent = false

  if (positionerParent) {
    this.positionerParent = positionerParent
  }

  this.initVisibility_()
  this.findPositioners_()
  this.attachEvents_()
}

/**
 * @enum {string}
 */
Popup.EventType = {
  BEFORE_OPEN: 'before-open',
  AFTER_OPEN: 'after-open',
  BEFORE_CLOSE: 'before-close',
  AFTER_CLOSE: 'after-close',
}

/**
 * @const
 * @type {string}
 */
Popup.CLASS_CLOSE_BUTTON = 'js-popup-close'

/**
 * @const
 * @type {string}
 */
Popup.CLASS_NOT_DISPLAY = 'not_display'

/**
 * @const
 * @type {string}
 */
Popup.CLASS_POSITIONER = 'popup_positioner'

/**
 * @returns {!jQuery} рутовый элемент
 */
Popup.prototype.getRoot = function () {
  return this.root_
}

/**
 * @returns {boolean} true если закрыт
 */
Popup.prototype.isClosed = function () {
  return this.closed_
}

Popup.prototype.toggle = function (target) {
  if (this.closed_) {
    this.open(target)
  } else {
    this.close(target)
  }
}

Popup.prototype.open = function (target) {
  /** @type {Popup} */
  var that = this,
    eventData

  if (!this.closed_ || this.aniInProgress_) {
    return
  }
  if (target) {
    eventData = { target: target }
  }
  if (this.duration_) {
    this.aniInProgress_ = true

    this.root_.css({
      'opacity': 0,
      'display': '',
    })

    this.positionIfPossible_()
    this.eventsDispatcher_.trigger(Popup.EventType.BEFORE_OPEN, eventData)

    this.root_.animate({ 'opacity': 1 }, this.duration_, 'linear', function () {
      that.closed_ = false
      that.aniInProgress_ = false
      that.eventsDispatcher_.trigger(Popup.EventType.AFTER_OPEN, eventData)
    })
  } else {
    this.root_.css({
      'visibility': 'hidden',
      'display': '',
    })

    this.positionIfPossible_()
    this.eventsDispatcher_.trigger(Popup.EventType.BEFORE_OPEN, eventData)

    this.closed_ = false
    this.root_.css('visibility', '')
    this.eventsDispatcher_.trigger(Popup.EventType.AFTER_OPEN, eventData)
  }
}

Popup.prototype.close = function (target) {
  /** @type {Popup} */
  var that = this,
    eventData

  if (this.closed_ || this.aniInProgress_) {
    return
  }

  if (target) {
    eventData = { target: target }
  }
  this.eventsDispatcher_.trigger(Popup.EventType.BEFORE_CLOSE, eventData)

  if (this.duration_) {
    this.aniInProgress_ = true

    this.root_.animate({ 'opacity': 0 }, this.duration_, 'linear', function () {
      that.root_.css('display', 'none')
      that.closed_ = true
      that.aniInProgress_ = false
      that.eventsDispatcher_.trigger(Popup.EventType.AFTER_CLOSE)
    })
  } else {
    this.closed_ = true
    if (that.root_.hasClass('animated')) {
      this.aniInProgress_ = true

      that.root_.one('animationend webkitAnimationEnd oAnimationEnd MSAnimationEnd', function () {
        if (that.closed_ === true) {
          that.root_.css('display', 'none')
          that.eventsDispatcher_.trigger(Popup.EventType.AFTER_CLOSE, eventData)
        }
        that.aniInProgress_ = false
      })
    } else {
      that.root_.css('display', 'none')
      that.eventsDispatcher_.trigger(Popup.EventType.AFTER_CLOSE, eventData)
    }
  }
}

/**
 * @param {Popup.EventType} eventType
 * @param {function(!jQuery.event)} callback
 */
Popup.prototype.addEventListener = function (eventType, callback) {
  this.eventsDispatcher_.bind(eventType, callback)
}

/**
 * @param {Popup.EventType} eventType
 * @param {function(!jQuery.event)} callback
 */
Popup.prototype.removeEventListener = function (eventType, callback) {
  this.eventsDispatcher_.unbind(eventType, callback)
}

/**
 * @private
 */
Popup.prototype.initVisibility_ = function () {
  if (this.closed_) {
    this.root_
      .css('display', 'none')
      .removeClass(Popup.CLASS_NOT_DISPLAY)
  } else {
    this.root_.css('display', '')
  }
}

/**
 * @private
 */
Popup.prototype.findPositioners_ = function () {
  if (!this.opener_) {
    return
  }

  /** @type {jQuery} */
  var positionerInsidePopup =
    this.root_.find('.' + Popup.CLASS_POSITIONER)

  /** @type {jQuery} */
  var positionerOutsidePopup = this.positionerParent ?
    this.opener_.closest(this.positionerParent).find('.' + Popup.CLASS_POSITIONER) :
    this.opener_.parent().find('.' + Popup.CLASS_POSITIONER)

  if (positionerInsidePopup.length) {
    this.positionerInsidePopup_ = positionerInsidePopup

    this.positionerOutsidePopup_ =
      positionerOutsidePopup.length ? positionerOutsidePopup : this.opener_
  }
}

/**
 * @private
 */
Popup.prototype.attachEvents_ = function () {
  /** @type {Popup} */
  var that = this

  if (this.opener_) {
    this.opener_.click(
      /**
       * @param {Object} event
       */
      function (event) {
        var target = event.currentTarget
        that.toggle($(target))
        event.preventDefault()
      })
  }

  this.closer_.click(
    function (event) {
      var target = event.currentTarget
      that.close($(target))
    })

  $(document).keyup(
    /**
     * @param {!jQuery.Event} event
     */
    function (event) {
      if (event.which === KEY_CODES.Escape) {
        that.close()
      }
    })

  this.externalClickCatcher_.click(
    /**
     * @param {!jQuery.event} event
     */
    function (event) {
      var excluded = that.exclideExternalClickCatcher_
      if (typeof excluded === 'string') {
        excluded = $(excluded)
      }
      if (
        typeof (that.exclideExternalClickCatcher_) === 'undefined' || !(
          excluded.has(event.target).length ||
          excluded.is(event.target)
        )
      ) {
        that.onCatcherClick_(event)
      }
    })
}

/**
 * @param {!jQuery.event} event
 *
 * @private
 */
Popup.prototype.onCatcherClick_ = function (event) {
  // which === 0: fix for jQuery's bug in IE8-
  /** @type {boolean} */
  var leftMouseButton = (event.which === 1 || event.which === 0)

  /** @type {boolean} */
  var insidePopup = (
    this.root_.is(event.target) || this.root_.has(event.target).length > 0) || !$(document).find(event.target).length

  /** @type {boolean} */
  var insideOpener = false
  if (this.opener_ !== null) {
    insideOpener = (
      this.opener_.is(event.target) ||
    this.opener_.has(event.target).length > 0)
  }

  /** @type {boolean} */
  var outside =
    (!insidePopup && !insideOpener) ||
    event.target === this.externalClickCatcher_[0]

  if (leftMouseButton && outside) {
    this.close()
  }
}

/**
 * @private
 */
Popup.prototype.positionIfPossible_ = function () {
  if (!this.positionerInsidePopup_ || !this.positionerOutsidePopup_) {
    return
  }
  var positionerOutsidePopupOffset =
    /** @type {{left:number, top:number}} */
    this.positionerOutsidePopup_.offset()

  var positionerInsidePopupOffset =
    /** @type {{left:number, top:number}} */
    this.positionerInsidePopup_.offset()

  var popupOffset =
    /** @type {{left:number, top:number}} */
    this.root_.offset()

  var leftOffset = positionerOutsidePopupOffset.left
  var topOffset = positionerOutsidePopupOffset.top
  if (!app.isDesktop) {
    leftOffset = leftOffset - $('body').position().left
    topOffset = topOffset - $('body').position().top
  }
  this.root_.css({
    left: (leftOffset + (popupOffset.left - positionerInsidePopupOffset.left)),
    top: (topOffset + (popupOffset.top - positionerInsidePopupOffset.top)),
  })
}
